import {
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  MailIcon,
  PhoneIcon,
  SearchIcon,
} from '@heroicons/react/solid'
import {
  cloneElement,
  type ComponentProps,
  InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  useState,
} from 'react'
import { classes, throwUnsupportedValue } from 'src/utils'
import { Label } from '../../ui/label'

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> & {
  label: string
  hideLabel?: boolean
  className?: string
  classNameInput?: string
  classNameLabel?: string
} & (
    | {
        value: string
        setValue: (value: string) => void
        type?: 'text' | 'password' | 'email' | 'url' | 'search' | 'tel'
      }
    | {
        value: number
        setValue: (value: number) => void
        type: 'number'
      }
  )

export function InputField({
  type,
  label,
  hideLabel,
  className,
  classNameInput,
  classNameLabel,
  setValue,
  value,
  ...props
}: Props) {
  const [hidePassword, setHidePassword] = useState(true)
  const icon = getInputIcon(type || 'text', hidePassword)

  return (
    <div className={classes('flex flex-col group', className)}>
      {!hideLabel && (
        <Label
          className={classes(
            'group-hover:text-slate-500 group-focus-within:text-theme-500 group-hover:group-focus-within:text-theme-500 mb-1',
            classNameLabel,
          )}
        >
          {label}
        </Label>
      )}

      <label
        className={classes(
          'relative flex flex-row items-center h-10 bg-white border rounded text-slate-600 border-slate-100 overflow-hidden',
          'active:shadow-none',
          'focus-within:border-theme-500 focus-within:ring-theme-500 focus-within:ring-1',
          props.disabled
            ? 'focus-within:border-slate-300 focus-within:ring-slate-300'
            : 'hover:shadow-sm hover:border-slate-200 hover:focus-within:border-theme-500',
          classNameInput,
        )}
      >
        {isValidElement(icon) &&
          cloneElement(icon, {
            'aria-hidden': true,
            className: classes(
              'box-content p-2 absolute top-0 bottom-0 my-autow-5 h-5 text-slate-400/75',
              type === 'password' && 'cursor-pointer',
            ),
            onClick: () => {
              if (type === 'password') {
                setHidePassword((prev) => !prev)
              }
            },
          })}

        <input
          value={value}
          onChange={(e) =>
            type === 'number'
              ? setValue(Number(e.target.value))
              : setValue(e.target.value)
          }
          type={
            type === 'password'
              ? hidePassword
                ? 'password'
                : 'text'
              : type || 'text'
          }
          required
          className={classes(
            icon ? 'pl-9 pr-3' : 'px-3',
            'w-full h-full focus:outline-none disabled:bg-slate-100 disabled:text-slate-600 disabled:shadow-none disabled:border-slate-100',
          )}
          {...props}
        />
      </label>
    </div>
  )
}

function getInputIcon(
  type: Exclude<Props['type'], undefined>,
  hidePassword?: boolean,
): ReactElement<ComponentProps<'svg'>> | null {
  switch (type) {
    case 'search':
      return <SearchIcon />
    case 'tel':
      return <PhoneIcon />
    case 'email':
      return <MailIcon />
    case 'password':
      return hidePassword ? <LockClosedIcon /> : <LockOpenIcon />
    case 'url':
      return <LinkIcon />
    case 'number':
    case 'text':
      return null
    default:
      return throwUnsupportedValue(type)
  }
}
